import React from 'react';
import {
  Sparkles, CheckCircle2, Lightbulb, Layers, RefreshCw, Save, Calendar, Clock, Target, Send
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export function WeekPlanTab({
  lessonForm, setLessonForm, loading, generateWeekPlan,
  generatedWeek, saveLesson, shareToAcademic, sharingToAcademic,
  getSubjectOptions, getYearOptions, shareLabel = 'Share to Academic'
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Week Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Weekly Topic *</Label>
            <Input
              placeholder="e.g., Introduction to Sewing"
              value={lessonForm.topic}
              onChange={(e) => setLessonForm({...lessonForm, topic: e.target.value})}
              data-testid="week-input-topic"
            />
          </div>

          <div className="space-y-2">
            <Label>Subject</Label>
            <Select
              value={lessonForm.subject}
              onValueChange={(value) => setLessonForm({...lessonForm, subject: value})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getSubjectOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Year Level</Label>
            <Select
              value={String(lessonForm.year_level)}
              onValueChange={(value) => setLessonForm({...lessonForm, year_level: parseInt(value)})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getYearOptions().map(year => (
                  <SelectItem key={year} value={String(year)}>Year {year}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Duration per Lesson</Label>
            <Select
              value={String(lessonForm.duration || 45)}
              onValueChange={(value) => setLessonForm({...lessonForm, duration: parseInt(value)})}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({length: 28}, (_, i) => 15 + i * 5).map(min => (
                  <SelectItem key={min} value={String(min)}>{min} min</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button 
            className="w-full mt-4" 
            onClick={generateWeekPlan}
            disabled={loading}
            data-testid="btn-generate-week"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Calendar className="h-4 w-4 mr-2" />
                Generate Week Plan
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <Card className="lg:col-span-3">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Weekly Lesson Plans
            </span>
            {generatedWeek && (
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => saveLesson({ title: `Week Plan: ${lessonForm.topic}` }, true, generatedWeek)}
                  data-testid="btn-save-week"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Save Week Plan
                </Button>
                {shareToAcademic && (
                  <Button 
                    variant="default" 
                    size="sm" 
                    onClick={() => shareToAcademic({ title: `Weekly Lesson Plans: ${lessonForm.topic}`, plans: generatedWeek })}
                    disabled={sharingToAcademic}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="btn-share-week-to-academic"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {sharingToAcademic ? 'Sharing...' : shareLabel}
                  </Button>
                )}
              </div>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!generatedWeek ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Generate a full week of connected lesson plans</p>
            </div>
          ) : (
            <div className="space-y-4">
              {generatedWeek.map((day, idx) => (
                <Card key={idx} className="border-l-4" style={{borderLeftColor: ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#f43f5e'][idx]}}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <Badge className="mb-2" style={{backgroundColor: ['#3b82f6','#10b981','#f59e0b','#8b5cf6','#f43f5e'][idx]}}>
                          {day.day}
                        </Badge>
                        <h4 className="font-semibold">{day.title}</h4>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => saveLesson(day)}
                          data-testid={`btn-save-day-${idx}`}
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                        {shareToAcademic && (
                          <Button 
                            variant="default" 
                            size="sm" 
                            onClick={() => shareToAcademic(day)}
                            disabled={sharingToAcademic}
                            className="bg-emerald-600 hover:bg-emerald-700"
                            data-testid={`btn-share-day-${idx}`}
                          >
                            <Send className="h-3 w-3 mr-1" />
                            Share
                          </Button>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div>
                        <h5 className="font-medium flex items-center gap-1 mb-1">
                          <Target className="h-3 w-3 text-blue-600" />
                          Objectives
                        </h5>
                        <ul className="space-y-1">
                          {day.objectives?.map((obj, i) => (
                            <li key={i} className="flex items-start gap-1 text-muted-foreground">
                              <CheckCircle2 className="h-3 w-3 text-green-500 mt-0.5 shrink-0" />
                              {obj}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h5 className="font-medium flex items-center gap-1 mb-1">
                          <Layers className="h-3 w-3 text-purple-600" />
                          Activities
                        </h5>
                        {day.activities?.map((act, i) => (
                          <div key={i} className="bg-gray-50 p-2 rounded mb-1">
                            <span className="font-medium">{act.name}</span>
                            <Badge variant="secondary" className="ml-2 text-xs">{act.duration}</Badge>
                            <p className="text-muted-foreground text-xs mt-1">{act.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                      <div className="bg-blue-50 p-2 rounded">
                        <span className="font-medium text-blue-700">Materials:</span> {day.materials}
                      </div>
                      <div className="bg-green-50 p-2 rounded">
                        <span className="font-medium text-green-700">Assessment:</span> {day.assessment}
                      </div>
                      <div className="bg-orange-50 p-2 rounded">
                        <span className="font-medium text-orange-700">Homework:</span> {day.homework}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
